import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import Header from './Header';
import Sidebar from './sidebar';
import Cookies from 'js-cookie';
import lottie from 'lottie-web';
import paperplaneData from '../assets/animation/paperplane.json';

// Import the new layout styles
import '../styles/Layout.css';

const getUserRole = () => {
    return Cookies.get('userRole');
};

const LottieLoader = ({ isVisible }) => {
  const animationContainer = useRef(null);
  const animationInstance = useRef(null);

  useEffect(() => {
    if (isVisible && animationContainer.current) {
      animationInstance.current = lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: paperplaneData,
      });
    }
    return () => {
      if (animationInstance.current) {
        animationInstance.current.destroy();
      }
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className="loading-overlay" style={{ opacity: isVisible ? 1 : 0, visibility: isVisible ? 'visible' : 'hidden' }}>
      <div ref={animationContainer} style={{ width: '600px', height: '600px', marginBottom: '30px' }} />
      <div className="loading-overlay-text">LOADING...</div>
    </div>
  );
};

export default function Layout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pageTitle, setPageTitle] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [globalSearch, setGlobalSearch] = useState('');
  const userRole = getUserRole();

  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) setSidebarCollapsed(true);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    const timer = setTimeout(() => setIsLoading(false), 3000);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
      clearTimeout(timer);
    };
  }, []);

  const outletContext = {
    setPageTitle,
    pageTitle,
    sidebarCollapsed,
    isMobile,
    showLoading: () => setIsLoading(true),
    hideLoading: () => setIsLoading(false),
    globalSearch,
    setGlobalSearch,
  };

  const mainContentStyle = {
    marginLeft: isMobile ? '0' : (sidebarCollapsed ? '70px' : '280px'),
    marginTop: '75px',
    minHeight: 'calc(100vh - 75px)',
    transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: isLoading ? 0.3 : 1,
    pointerEvents: isLoading ? 'none' : 'auto',
    filter: isLoading ? 'blur(2px)' : 'none',
  };

  return (
    <div id="layout-container" className={sidebarCollapsed ? 'sidebar-collapsed' : ''}>
      <LottieLoader isVisible={isLoading} />
      <Sidebar role={userRole} />
      
      <div className="main-wrapper" style={{ marginLeft: mainContentStyle.marginLeft, transition: mainContentStyle.transition }}>
        <Header
          pageTitle={pageTitle}
          isMobile={isMobile}
          globalSearch={globalSearch}
          setGlobalSearch={setGlobalSearch}
        />
        <main
          className="content-wrapper"
          style={{...mainContentStyle, marginLeft: 0}} // main wrapper handles margin
        >
          <div className="content-container">
            <div
              className="glass-card"
              style={{ padding: isMobile ? '20px' : '32px' }}
            >
              <Outlet context={outletContext} />
            </div>
          </div>
          <ScrollToTopButton />
        </main>
      </div>
    </div>
  );
}

const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const mainContentRef = useRef(null);

  useEffect(() => {
    mainContentRef.current = document.querySelector('.content-wrapper');
    const toggleVisibility = () => {
      if (mainContentRef.current && mainContentRef.current.scrollTop > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    mainContentRef.current?.addEventListener('scroll', toggleVisibility);
    return () => mainContentRef.current?.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    mainContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!isVisible) return null;
  return (
    <button onClick={scrollToTop} className="scroll-to-top-button">
      ↑
    </button>
  );
};
